import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { fetchSpreadsheets } from '../api/mastersheetApi';
import { Menu, X, FileSpreadsheet, ChevronDown, ChevronRight, LayoutDashboard, FileText, Target, Library, CircleHelp, Settings, LogOut, MessageSquareText, Calendar, UsersRound, Calculator, Link2, Minus, Search, CircleDollarSign, UserSearch, Users, FileChartLine } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../img/PlanSync-Logo-New-P.png';
import { useCompany } from '../contexts/CompanyContext';

const Sidebar = ({ isOpen, setIsOpen, username, activeSpreadsheet, setActiveSpreadsheet, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [spreadsheets, setSpreadsheets] = useState([]);
  const [isSpreadsheetExpanded, setIsSpreadsheetExpanded] = useState(true);
  const [isGrowBusinessExpanded, setIsGrowBusinessExpanded] = useState(true);
  const [isCalculatorsExpanded, setIsCalculatorsExpanded] = useState(false);
  const { isCustomerView } = useCompany();

  useEffect(() => {
    const loadSpreadsheets = async () => {
      try {
        const fetchedSpreadsheets = await fetchSpreadsheets();
        setSpreadsheets(fetchedSpreadsheets);
      } catch (error) {
        console.error("Error fetching spreadsheets:", error);
      }
    };

    
    loadSpreadsheets();
  }, [username]);

  useEffect(() => {
    if (location.pathname !== '/') {
      setActiveSpreadsheet(null);
    }
  }, [location.pathname, setActiveSpreadsheet]);

  useEffect(() => {
    if (!activeSpreadsheet && location.pathname === '/') {
      setActiveSpreadsheet("Plan Touchpoints");
    }
  }, [location.pathname, activeSpreadsheet, setActiveSpreadsheet]);

  const sidebarVariants = {
    open: { x: 0, opacity: 1 },
    closed: { x: "-100%", opacity: 0 }
  };

  const listItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const handleSpreadsheetClick = (spreadsheetId) => {
    setActiveSpreadsheet(spreadsheetId);
    const formattedName = spreadsheetId.toLowerCase().replace(/ /g, '-');
    navigate(`/spreadsheet/${formattedName}`);
  };

  const spreadsheetItemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
  };

  const isSpreadsheetActive = (sheetName) => {
    // Get the current spreadsheet name from URL if we're on a spreadsheet page
    const urlPath = location.pathname;
    const urlSpreadsheetName = urlPath.startsWith('/spreadsheet/') 
      ? urlPath.replace('/spreadsheet/', '').replace(/-/g, ' ')
      : '';

    // Check if either the activeSpreadsheet matches or the URL matches
    return sheetName.toLowerCase() === urlSpreadsheetName ||
           (activeSpreadsheet === sheetName && location.pathname === '/');
  };



  const filterPlanDataSpreadsheets = (sheets) => {
    const excludedSheets = ['Prospecting', 'Participants Table'];
    const filteredSheets = sheets.filter(sheet => !excludedSheets.includes(sheet));
    
    // Move Client List to the front while preserving order of other items
    const reorderedSheets = filteredSheets.filter(sheet => sheet !== 'Client List');
    return ['Client List', ...reorderedSheets];
  };

  return (
    <>
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
            onClick={toggleSidebar}
            className="fixed top-4 left-4 bg-white/90 text-gray-600 hover:text-sky-500 hover:bg-white rounded-xl p-3 z-[53] transition-all duration-300 ease-in-out shadow-lg backdrop-blur-sm"
          >
            <Menu size={24} />
          </motion.button>
        )}
      </AnimatePresence>

      <motion.div
        initial="closed"
        animate={isOpen ? "open" : "closed"}
        variants={sidebarVariants}
        transition={{ duration: 0.3, type: "tween" }}
        className="fixed left-0 top-0 h-screen bg-gradient-to-bl from-white via-gray-50 to-stone-100 text-gray-700 z-[52] flex flex-col w-80 shadow-2xl shadow-gray-400/20"
      >
        {isOpen && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            onClick={toggleSidebar}
            className="absolute top-6 right-6 rounded-xl p-2 hover:bg-gray-100 transition-all duration-300 backdrop-blur-sm text-gray-600"
          >
            <X size={20} />
          </motion.button>
        )}
        
        <div className="px-6 pt-8 pb-4 border-b border-gray-200">
          <img src={Logo} alt="PlanSync AI" style={{ width: '10rem' }} />
        </div>

        <nav className="p-6 flex-grow overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
          <motion.ul className="space-y-2">
            <div className="py-3">
              <div className="text-xs font-medium text-gray-500 px-3">360° PLAN VIEW</div>
            </div>
            <motion.li variants={listItemVariants} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
              <button
                onClick={() => setIsSpreadsheetExpanded(!isSpreadsheetExpanded)}
                className={`flex items-center justify-between w-full space-x-3 p-3 rounded-xl transition-all duration-300 ${
                  location.pathname === '/' 
                    ? 'bg-sky-100 backdrop-blur-sm font-medium' 
                    : 'hover:bg-gray-300'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <FileSpreadsheet size={20} className="text-sky-600" />
                  <span>Plan Data</span>
                </div>
                {isSpreadsheetExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>
              
              <AnimatePresence>
                {isSpreadsheetExpanded && (
                  <motion.ul
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={{
                      visible: { opacity: 1, height: 'auto' },
                      hidden: { opacity: 0, height: 0 }
                    }}
                    transition={{ duration: 0.3 }}
                    className="ml-4 mt-2 space-y-1 overflow-hidden"
                  >
                    {spreadsheets && filterPlanDataSpreadsheets(spreadsheets).map((sheet, index) => (
                      <motion.li
                        key={sheet}
                        variants={spreadsheetItemVariants}
                        transition={{ delay: index * 0.05 }}
                      >
                        <button
                          onClick={() => handleSpreadsheetClick(sheet)}
                          className={`w-full text-left py-2.5 px-3 rounded-xl transition-all duration-300 ${
                            isSpreadsheetActive(sheet)
                              ? 'bg-sky-100 backdrop-blur-sm font-medium' 
                              : 'hover:bg-gray-300'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <Minus size={16} className="text-gray-400" />
                            <span className="text-sm">{sheet}</span>
                          </div>
                        </button>
                      </motion.li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </motion.li>

            

            {[
              { to: "/file-library", icon: Library, text: "File Library" },

              { to: "/reporting", icon: FileText, text: "Reporting" },

              
              { type: 'divider', text: 'RESOURCES' },


              // {
              //   type: 'dropdown',
              //   icon: UsersRound,
              //   text: "Participant Census",
              //   isExpanded: isParticipantCensusExpanded,
              //   onToggle: () => setIsParticipantCensusExpanded(!isParticipantCensusExpanded),
              //   items: [
              //     {
              //       type: 'link',
              //       text: "Send Participant Census",
              //       to: "/participant-census",
              //       icon: UsersRound
              //     },
              //     {
              //       type: 'spreadsheet',
              //       text: "Census Response Data",
              //       onClick: () => handleSpreadsheetClick("Participants Table")
              //     }
              //   ]
              // },
              { to: "/resources", icon: Link2, text: "Advisor Resource Hub" },
              
              // {
              //   type: 'dropdown',
              //   icon: Target,
              //   text: "Grow My Business",
              //   isExpanded: isGrowBusinessExpanded,
              //   onToggle: () => setIsGrowBusinessExpanded(!isGrowBusinessExpanded),
              //   items: [
              //     {
              //       type: 'link',
              //       text: "Marketing Materials",
              //       to: "/marketing",
              //       icon: Target
              //     },
              //     
              //   ]
              // },
              
              // { type: 'divider', text: 'DISCOVER' },
              // { to: "/fund-research", icon: CircleDollarSign, text: "Fund Research" },
              // { to: "/plan-search", icon: Search, text: "Plan Search" },
              // { to: "/wholesaler-search", icon: UserSearch, text: "Wholesaler Search" },
              // { to: "/proposal-automation", icon: FileChartLine, text: "Proposal Automation" },
              // { to: "/participant-census", icon: Users, text: "Participant Census" },
              

              
              ...(!isCustomerView ? [
                { type: 'divider', text: 'VISUALIZATIONS' },
                { to: "/data-dashboard", icon: LayoutDashboard, text: "Data Dashboard" }, 
                { to: "/requirements-calendar", icon: Calendar, text: "Requirements Calendar" }
              ] : []),
              {
                type: 'dropdown',
                icon: Calculator,
                text: "Calculators",
                isExpanded: isCalculatorsExpanded,
                onToggle: () => setIsCalculatorsExpanded(!isCalculatorsExpanded),
                items: [
                  {
                    type: 'link',
                    text: "Fee Calculator",
                    to: "/calculators/fee-calculator",
                    icon: Calculator
                  },
                  {
                    type: 'link',
                    text: "Profitability Calculator",
                    to: "/calculators/profitability-calculator",
                    icon: Calculator
                  },
                  {
                    type: 'link',
                    text: "Retirement Readiness",
                    to: "/calculators/retirement-readiness",
                    icon: Calculator
                  },
                  {
                    type: 'link',
                    text: "Revenue Forecast",
                    to: "/calculators/revenue-forecast",
                    icon: Calculator
                  }
                ]
              },
              // { to: "/fee-benchmarking", icon: Calculator, text: "Fee Benchmarking" },
              
              { type: 'divider', text: 'SUPPORT' },
              
              { to: "/feedback", icon: MessageSquareText, text: "User Feedback" },
              { to: "/help", icon: CircleHelp, text: "Help" },

              ...(!isCustomerView ? [
                { type: 'divider', text: 'ADMIN' },
                { to: "/settings", icon: Settings, text: "Settings" }
              ] : []),
            ].map((item, index) => (
              item.type === 'divider' ? (
                <div key={item.text} className="py-3">
                  <div className="text-xs font-medium text-gray-500 px-3">{item.text}</div>
                </div>
              ) : item.type === 'dropdown' ? (
                <motion.li
                  key={item.text}
                  variants={listItemVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.2 + index * 0.1 }}
                >
                  <button
                    onClick={item.onToggle}
                    className={`flex items-center justify-between w-full space-x-3 p-3 rounded-xl transition-all duration-300 ${
                      item.isExpanded 
                        ? 'backdrop-blur-sm font-medium' 
                        : 'hover:bg-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <item.icon size={20} className="text-sky-600" />
                      <span>{item.text}</span>
                    </div>
                    {item.isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  
                  <AnimatePresence>
                    {item.isExpanded && (
                      <motion.ul
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={{
                          visible: { opacity: 1, height: 'auto' },
                          hidden: { opacity: 0, height: 0 }
                        }}
                        transition={{ duration: 0.3 }}
                        className="ml-4 mt-2 space-y-1 overflow-hidden"
                      >
                        {item.items.map((subItem) => (
                          <motion.li
                            key={subItem.text}
                            variants={spreadsheetItemVariants}
                          >
                            {subItem.type === 'link' ? (
                              <Link
                                to={subItem.to}
                                className={`flex items-center w-full space-x-2 py-2.5 px-3 rounded-xl transition-all duration-300 ${
                                  location.pathname === subItem.to
                                    ? 'bg-sky-100 backdrop-blur-sm font-medium'
                                    : 'hover:bg-gray-300'
                                }`}
                              >
                                <Minus size={16} className="text-gray-400" />
                                <span className="text-sm">{subItem.text}</span>
                              </Link>
                            ) : (
                              <button
                                onClick={subItem.onClick}
                                className={`w-full text-left py-2.5 px-3 rounded-xl transition-all duration-300 ${
                                  isSpreadsheetActive(subItem.text)
                                    ? 'bg-sky-100 backdrop-blur-sm font-medium' 
                                    : 'hover:bg-gray-300'
                                }`}
                              >
                                <div className="flex items-center space-x-2">
                                  <Minus size={16} className="text-gray-400" />
                                  <span className="text-sm">{subItem.text}</span>
                                </div>
                              </button>
                            )}
                          </motion.li>
                        ))}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </motion.li>
              ) : (
                <motion.li
                  key={item.to}
                  variants={listItemVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.2 + index * 0.1 }}
                >
                  <Link
                    to={item.to}
                    className={`flex items-center space-x-3 p-3 rounded-xl transition-all duration-300 ${
                      location.pathname === item.to
                        ? 'bg-sky-100 backdrop-blur-sm font-medium border border-sky-200'
                        : 'hover:bg-gray-300'
                    }`}
                  >
                    <item.icon size={20} className="text-sky-600" />
                    <span>{item.text}</span>
                  </Link>
                </motion.li>
              )
            ))}
          </motion.ul>
        </nav>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="p-6 border-t border-gray-200"
        >
          {/* <div className="flex items-center space-x-3 mb-4 px-2">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              {username?.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="text-sm font-medium">{username}</div>
              <div className="text-xs text-blue-200/70">Administrator</div>
            </div>
          </div> */}
          <button 
            onClick={onLogout}
            className="flex items-center w-full space-x-3 p-3 rounded-xl transition-all duration-300 hover:bg-gray-100 text-sm"
          >
            <LogOut size={18} className="text-red-600" />
            <span>Logout</span>
          </button>
        </motion.div>
      </motion.div>
    </>
  );
};

export default Sidebar;